import { createEffect, createMemo, createUniqueId, Match, Switch, untrack } from "solid-js";
import { type TranslationKey, useTranslation } from "../../../state/i18n";
import { PillSingleSelect, SingleSelect } from "../../../components/menu/Select";
import DismissiblePill from "../../../components/pill/DismissiblePill";
import { CompactTextInput } from "../../../components/inputs/TextInput";
import { Form } from "../../../components/form/Form";
import { any, literal, nonOptional, object, pipe, string, transform, variant } from "valibot";
import { ErrorMessage } from "../../../components/inputs/Input";
import { CompactDateInput, CompactDateTimeInput, CompactTimeInput } from "../../../components/inputs/DateInput";
import { CompactNumberInput } from "../../../components/inputs/NumberInput";
import { DEFAULT_DATE_TIME_FORMAT } from "../../../components/formatting/FormatDate";
import { findOrThrow } from "../../../utils/arrayUtil";
import { errorTranslation } from "../../../utils/validation/base";
import type { Filter, FilterDefinition, FilterOperator } from "./filter";
import { isEmpty, isNil } from "lodash-es";
import { createWritableMemo } from "@solid-primitives/memo";
import type { PartialBy } from "../../../types";
import type { ColumnDefWithId } from "./Table";
import type { RowData } from "@tanstack/solid-table";
import Popover from "../../../components/popover/Popover";
import { TextButton } from "../../../components/button/Button";
import { narrow } from "../../../utils/solidUtils";
import Text from "../../../components/text/Text";
import { createForm } from "../../../components/form/createForm";
import { keyFromSelector } from "i18next";

const OPERATOR_TRANSLATIONS: Record<FilterOperator, TranslationKey> = {
  isFilledIn: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_IS_FILLED_IN_LABEL),
  is: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_IS_LABEL),
  isNot: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_IS_NOT_LABEL),
  contains: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_CONTAINS_LABEL),
  startsWith: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_STARTS_WITH_LABEL),
  endsWith: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_ENDS_WITH_LABEL),
  greaterThan: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_GREATER_THAN_LABEL),
  lessThan: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_LESS_THAN_LABEL),
  isBefore: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_IS_BEFORE_LABEL),
  isAfter: keyFromSelector(($) => $.TABLE_FILTER_OPERATOR_IS_AFTER_LABEL),
};

const TableActiveFilter = <T extends RowData>(props: {
  filter: PartialBy<Filter, "value">;
  columnDef: ColumnDefWithId<T>;
  onSave: (filter: Filter) => void;
  onClose: (open: boolean) => void;
  onRemove: () => void;
  open: boolean;
}) => {
  const { t } = useTranslation();
  const filterDefinitions = untrack(() => props.columnDef.meta.filter.definition);

  const schema = variant(
    "operator",
    filterDefinitions.map((def) => {
      // Filter values are stored as strings, so we wrap 'number' based filters with a transformation from 'string' to
      // 'number'
      const valueSchema =
        !isNil(def.schema) && def.inputType === "number"
          ? pipe(
              string(),
              transform((input) => Number(input)),
              def.schema,
            )
          : def.schema;

      return object({
        operator: literal(def.operator),
        value: nonOptional(valueSchema ?? any(), errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
      });
    }),
  );

  const form = createForm<{
    operator: FilterOperator;
    value: string;
  }>(
    schema,
    ({ value, operator }) => {
      props.onSave({ ...props.filter, value, operator });
    },
    untrack(() => ({
      operator: props.filter.operator,
      value: props.filter.value,
    })),
  );

  const selectedFilterDefinition = createMemo(() =>
    findOrThrow(filterDefinitions, ({ operator }) => operator === form.getValue("operator")),
  );

  const [open, setOpen] = createWritableMemo(() => props.open);

  const formatValue = (filterDefinition: FilterDefinition, value: string) => {
    if (filterDefinition.inputType === "select") {
      return t(findOrThrow(filterDefinition.options, ({ id }) => id === value).label);
    } else if (filterDefinition.inputType === "datetime") {
      return DEFAULT_DATE_TIME_FORMAT.format(new Date(value));
    } else {
      return value;
    }
  };
  const displayValue = createMemo(() => {
    const operator = open() ? form.getValue("operator")! : props.filter.operator;
    const rawValue = open() ? form.getValue("value") : props.filter.value;
    const formattedValue = isNil(rawValue) ? "" : formatValue(selectedFilterDefinition(), rawValue);

    return t(OPERATOR_TRANSLATIONS[operator]) + " " + formattedValue;
  });

  createEffect(() => {
    if (!open()) {
      // Ensure the form and table filter state are in sync
      form.reset({ operator: props.filter.operator, value: props.filter.value });
    }
  });

  return (
    <>
      {/* We need this around because we need the functions, would be nicer if we could call the functions directly on `form`*/}
      <Form form={form}>
        {({ value, updateValue, error }) => {
          const inputId = createUniqueId();

          return (
            <Popover
              variant="md"
              Title={() => (
                <div class="flex min-w-0 items-center gap-2">
                  <Text text={props.columnDef.meta.name} truncate class="text-header-md text-primary" />
                  <PillSingleSelect<FilterOperator>
                    disabled={false}
                    ariaLabel={t(($) => $.TABLE_FILTER_POPOVER_FILTER_OPERATOR_ARIA_LABEL)}
                    options={filterDefinitions.map(({ operator }) => ({
                      variant: "text",
                      value: operator,
                      label: t(OPERATOR_TRANSLATIONS[operator]),
                    }))}
                    onSelect={(operator) => {
                      updateValue("value", undefined);
                      updateValue("operator", operator);
                    }}
                    value={value("operator")}
                  />
                </div>
              )}
              aria-label={t(($) => $.TABLE_FILTER_POPOVER_TITLE, { columnName: props.columnDef.meta.name })}
              open={open()}
              onOpenChange={(open) => {
                setOpen(open);
                props.onClose(open);
              }}
              positioning={{ placement: "bottom-start" }}
              Trigger={(triggerProps) => (
                <DismissiblePill
                  icon={props.columnDef.meta?.icon}
                  label={props.columnDef.meta.name}
                  value={displayValue()}
                  actionButton={{
                    ...triggerProps,
                    onClick: () => {
                      setOpen(true);
                      props.onClose(true);
                    },
                    "aria-label": t(($) => $.TABLE_FILTER_SHOW_ARIA_LABEL, { columnName: props.columnDef.meta.name }),
                  }}
                  closeButton={{
                    onClick: () => {
                      props.onRemove();
                    },
                    "aria-label": t(($) => $.TABLE_FILTER_REMOVE_ARIA_LABEL, { columnName: props.columnDef.meta.name }),
                  }}
                />
              )}
            >
              <>
                <div class="my-2">
                  <div class="flex flex-col gap-1">
                    <Switch>
                      <Match when={selectedFilterDefinition().inputType === "text"}>
                        <CompactTextInput
                          id={inputId}
                          ariaLabel={t(($) => $.TABLE_FILTER_POPOVER_FILTER_VALUE_ARIA_LABEL)}
                          value={value("value")}
                          placeholder={t(($) => $.TABLE_FILTER_TEXT_PLACEHOLDER)}
                          onBlur={(event) => {
                            updateValue("value", event.target.value);
                          }}
                          onInput={(event) => {
                            updateValue("value", event.target.value);
                          }}
                          errorMessage={error("value")}
                        />
                      </Match>

                      <Match when={narrow(selectedFilterDefinition(), (def) => def.inputType === "number")}>
                        {(def) => (
                          <CompactNumberInput
                            id={inputId}
                            ariaLabel={t(($) => $.TABLE_FILTER_POPOVER_FILTER_VALUE_ARIA_LABEL)}
                            value={isNil(value("value")) ? null : parseFloat(value("value")!)}
                            onBlur={(value) => {
                              updateValue("value", value?.toString());
                            }}
                            onInput={(value) => updateValue("value", value?.toString())}
                            allowDecimal={def().allowDecimal ?? true}
                            minValue={-Infinity}
                            maxValue={Infinity}
                            errorMessage={error("value")}
                          />
                        )}
                      </Match>

                      <Match when={narrow(selectedFilterDefinition(), (def) => def.inputType === "select")}>
                        {(def) => (
                          <SingleSelect
                            value={value("value")}
                            placeholder={t(
                              def().placeholderLabel ?? keyFromSelector(($) => $.TABLE_FILTER_SELECT_PLACEHOLDER),
                            )}
                            onSelect={(value) => updateValue("value", value)}
                            options={def().options.map((option) => ({
                              variant: "text",
                              value: option.id,
                              label: t(option.label),
                            }))}
                            ariaLabel={t(($) => $.TABLE_FILTER_POPOVER_FILTER_VALUE_ARIA_LABEL)}
                            hasError={!isNil(error("value"))}
                            sameWidth={false}
                          />
                        )}
                      </Match>

                      <Match when={selectedFilterDefinition().inputType === "date"}>
                        <CompactDateInput
                          id={inputId}
                          ariaLabel={t(($) => $.TABLE_FILTER_POPOVER_FILTER_VALUE_ARIA_LABEL)}
                          value={value("value") ?? ""}
                          onBlur={(event) => {
                            const { value } = event.target;
                            if (!isEmpty(value)) {
                              updateValue("value", value);
                            }
                          }}
                          errorMessage={error("value")}
                        />
                      </Match>

                      <Match when={selectedFilterDefinition().inputType === "time"}>
                        <CompactTimeInput
                          id={inputId}
                          ariaLabel={t(($) => $.TABLE_FILTER_POPOVER_FILTER_VALUE_ARIA_LABEL)}
                          value={value("value") ?? ""}
                          onBlur={(event) => {
                            const { value } = event.target;
                            if (!isEmpty(value)) {
                              updateValue("value", value);
                            }
                          }}
                          errorMessage={error("value")}
                        />
                      </Match>

                      <Match when={selectedFilterDefinition().inputType === "datetime"}>
                        <CompactDateTimeInput
                          id={inputId}
                          ariaLabel={t(($) => $.TABLE_FILTER_POPOVER_FILTER_VALUE_ARIA_LABEL)}
                          value={value("value") ?? ""}
                          onBlur={(event) => {
                            const { value } = event.target;
                            if (!Number.isNaN(Date.parse(value))) {
                              updateValue("value", value);
                            }
                          }}
                          errorMessage={error("value")}
                        />
                      </Match>
                    </Switch>

                    <ErrorMessage errorMessage={error("value")} />
                  </div>
                </div>

                <div class="mt-4 flex justify-end gap-1">
                  <TextButton
                    size="medium"
                    text={t(($) => $.TABLE_FILTER_POPOVER_RESET_ACTION_LABEL)}
                    variant="secondary"
                    onClick={() => {
                      form.reset({ operator: props.filter.operator, value: props.filter.value });
                    }}
                  />

                  <TextButton
                    text={t(($) => $.TABLE_FILTER_POPOVER_APPLY_ACTION_LABEL)}
                    disabled={form.submitDisabled()}
                    loading={form.isSubmitting()}
                    size="medium"
                    form={form.id}
                    type="submit"
                  />
                </div>
              </>
            </Popover>
          );
        }}
      </Form>
    </>
  );
};

export default TableActiveFilter;
